import React, { useCallback } from 'react';
import { Checkbox, ConfigProvider, Input, InputNumber, Radio, Select, Spin, Switch } from 'antd';
import Tooltip from '../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { MergeCellsOutlined, SplitCellsOutlined } from '@ant-design/icons';
import { useDrag, useDrop } from 'react-dnd';
import { PRIMARY_DARK } from '../../constants/constants';
import { hexToRgba } from '../../utils/theme';

/**
 * Les contrôles Ant Design du configurateur de tableau, dans le contrat attendu
 * par `src/shared/tables/ui/TableConfigBody`. Le rendu du Proposal ne change pas :
 * ce fichier ne fait que sortir les contrôles de la disposition, désormais
 * partagée avec l'éditeur web.
 */

// Type react-dnd dédié au réordonnancement des colonnes (distinct des autres
// sources de drag de l'éditeur).
export const COLUMN_DND_TYPE = 'table-config-column';

// La macro `icon()` n'accepte qu'un nom littéral : les icônes se résolvent ici,
// une fois, et pas au moment du rendu.
const ICONS = {
  reset: icon({ name: 'rotate-left', style: 'solid' }),
  up: icon({ name: 'chevron-up', style: 'solid' }),
  down: icon({ name: 'chevron-down', style: 'solid' }),
  'align-start': icon({ name: 'align-left', style: 'solid' }),
  'align-center': icon({ name: 'align-center', style: 'solid' }),
  'align-end': icon({ name: 'align-right', style: 'solid' }),
};

// Fusion : les icônes des boutons du panneau de droite (NavbarTable).
const COMPONENT_ICONS = {
  merge: <MergeCellsOutlined />,
  unmerge: <SplitCellsOutlined />,
};

export const tableConfigAntdWidgets = {
  Grip: () => <FontAwesomeIcon icon={icon({ name: 'grip-vertical', style: 'solid' })} />,

  Spinner: () => <Spin />,

  Checkbox: ({ checked, onChange, ariaLabel }) => (
    <Checkbox checked={checked} onChange={onChange} aria-label={ariaLabel} />
  ),

  TextArea: ({ value, onChange, ariaLabel }) => (
    <Input.TextArea
      autoSize={{ minRows: 1, maxRows: 3 }}
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: 12, resize: 'none' }}
    />
  ),

  NumberInput: ({ value, min, max, disabled, title, ariaLabel, onChange }) => (
    <Tooltip title={title}>
      <InputNumber
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={onChange}
        style={{ width: 88, fontSize: 14 }}
        addonAfter="%"
      />
    </Tooltip>
  ),

  Radio: ({ value, options, onChange, ariaLabel }) => (
    <Radio.Group value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel}>
      {options.map((o) => (
        <Radio.Button key={String(o.value)} value={o.value} disabled={o.disabled}>
          {o.label}
        </Radio.Button>
      ))}
    </Radio.Group>
  ),

  // Surcharge locale : le thème global dérive controlItemBgActive d'un gris
  // depuis PRIMARY_DARK (bleu marine trop foncé pour l'algo antd).
  Select: ({ value, options, onChange, ariaLabel }) => (
    <ConfigProvider theme={{ token: { controlItemBgActive: hexToRgba(PRIMARY_DARK, 0.12) } }}>
      <Select
        style={{ width: '100%' }}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        options={options}
      />
    </ConfigProvider>
  ),

  Switch: ({ checked, disabled, onChange, ariaLabel }) => (
    <Switch checked={checked} disabled={disabled} onChange={onChange} aria-label={ariaLabel} />
  ),

  IconButton: ({ name, title, disabled, ariaLabel, onClick }) => (
    <Tooltip title={title}>
      {/* span wrapper : le <svg> de FontAwesome ne relaie pas toujours le
          tooltip/ref d'antd -> on l'enveloppe pour un survol fiable. */}
      <span
        role="button"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          color: disabled ? '#ccc' : PRIMARY_DARK,
          flexShrink: 0,
          display: 'inline-flex',
        }}
      >
        <FontAwesomeIcon icon={ICONS[name] || ICONS.reset} />
      </span>
    </Tooltip>
  ),

  TextButton: ({ name, title, disabled, onClick, children }) => (
    <Tooltip title={title}>
      <span
        role="button"
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12,
          cursor: disabled ? 'default' : 'pointer',
          color: disabled ? '#ccc' : PRIMARY_DARK,
        }}
      >
        {COMPONENT_ICONS[name] || <FontAwesomeIcon icon={ICONS[name] || ICONS.reset} />}
        {children}
      </span>
    </Tooltip>
  ),
};

/**
 * Réordonnancement par drag, version react-dnd : la poignée tire, la ligne
 * accueille. Le survol réordonne en direct (le commit, lui, est debouncé).
 */
export function createAntdDragAdapter({ onMoveByDrag, onDropEnd }) {
  return {
    useColumnDrag(label) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [{ isDragging }, drag, preview] = useDrag(
        () => ({
          type: COLUMN_DND_TYPE,
          item: { label },
          collect: (monitor) => ({ isDragging: monitor.getItem()?.label === label }),
          end: () => onDropEnd(),
        }),
        [label, onDropEnd],
      );
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [, drop] = useDrop(
        () => ({
          accept: COLUMN_DND_TYPE,
          hover: ({ label: draggedLabel }) => {
            if (draggedLabel !== label) onMoveByDrag(draggedLabel, label);
          },
        }),
        [label, onMoveByDrag],
      );
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const rowRef = useCallback((node) => { drop(preview(node)); }, [drop, preview]);

      return {
        rowProps: { ref: rowRef },
        handleProps: { ref: drag },
        dragging: isDragging,
        dropTarget: false,
      };
    },
  };
}
